/**
 * Knowledge Base — replaces the old VaultPage.
 *
 * Three tabs: Documents, Templates, Sentinel. Single Upload drawer
 * shared across the page. All data fetched from the new
 * /api/vault/* + /api/sentinel/* surfaces.
 *
 * NOTE on routing: this file keeps the `VaultPage` export so the
 * existing `/v2/vault` / `/chat/vault` routes in App.js work
 * unchanged. The "vault" name lives on in code only — every
 * user-facing string is "Knowledge Base".
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  FileText,
  Folder,
  Layers,
  Loader2,
  Lock,
  MoreHorizontal,
  Plus,
  Search,
  Send,
  Shield,
  Sparkles,
  Star,
  Trash2,
  UploadCloud,
  X,
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const API = getBackendBaseUrl();

// ────────────────────────────────────────────────────────────────────
// Tiny fetch helpers — colocated to keep this file self-contained.
// ────────────────────────────────────────────────────────────────────
async function _json(url, opts = {}, token) {
  const headers = {
    ...(opts.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const r = await fetch(url, { ...opts, headers });
  if (!r.ok) throw new Error(`${r.status}`);
  // Defensive: clone before parsing so any prior body consumption
  // (Emergent RUM, devtools, extensions) doesn't crash us.
  try { return await r.clone().json(); }
  catch { return r.json(); }
}

const formatBytes = (n) => {
  if (!n) return '0 KB';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return '—'; }
};

const formatRelative = (iso) => {
  if (!iso) return 'Never';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return '—'; }
};

// ────────────────────────────────────────────────────────────────────
// PAGE
// ────────────────────────────────────────────────────────────────────
const VaultPage = () => {
  const { session, user } = useAuth();
  const token = session?.access_token;
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [tab, setTab] = useState('documents');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [health, setHealth] = useState({
    total_documents: 0, total_templates: 0,
    conflicted_count: 0, processing_count: 0,
    recent_activity: [], guardian_enabled: false,
  });
  const pollRef = useRef(null);

  // ── Data loaders ──────────────────────────────────────────────────
  const loadDocuments = useCallback(async () => {
    if (!token) return;
    try {
      const data = await _json(`${API}/api/vault/documents-by-type`, {}, token);
      setDocuments(data.documents || []);
    } catch { /* non-fatal */ }
  }, [token]);

  const loadTemplates = useCallback(async () => {
    if (!token) return;
    try {
      const data = await _json(`${API}/api/vault/templates`, {}, token);
      setTemplates(data.templates || []);
    } catch { /* non-fatal */ }
  }, [token]);

  const loadHealth = useCallback(async () => {
    if (!token) return;
    try {
      const data = await _json(`${API}/api/sentinel/health`, {}, token);
      setHealth(data || {});
    } catch { /* non-fatal */ }
  }, [token]);

  useEffect(() => {
    loadDocuments();
    loadTemplates();
    loadHealth();
  }, [loadDocuments, loadTemplates, loadHealth]);

  // ── Smart polling: while any doc is `processing`, refetch every 10s
  //    so Sentinel's status transitions surface without a manual reload.
  useEffect(() => {
    const anyProcessing =
      documents.some((d) => d.sentinel_status === 'processing') ||
      health.processing_count > 0;
    if (!anyProcessing) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(() => {
      loadDocuments();
      loadTemplates();
      loadHealth();
    }, 10_000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [documents, health.processing_count, loadDocuments, loadTemplates, loadHealth]);

  // ── Tab handlers ──────────────────────────────────────────────────
  const onUploaded = () => {
    setDrawerOpen(false);
    loadDocuments();
    loadTemplates();
    loadHealth();
  };

  return (
    <div
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="knowledge-base-page"
      className="min-h-screen bg-[#FAFAF8]"
    >
      <div className="max-w-[1180px] mx-auto px-6 py-8">
        {/* Header */}
        <header className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1
              className="text-[28px] font-semibold text-[#1B1B1A]"
              data-testid="kb-title"
            >
              {isAr ? 'قاعدة المعرفة' : 'Knowledge Base'}
            </h1>
            <p
              className="text-[13.5px] text-[#666] mt-1 flex items-center gap-1.5"
              data-testid="kb-subtitle"
            >
              <Shield className="w-3.5 h-3.5" /> {isAr ? 'يديرها Sentinel' : 'Managed by Sentinel'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            data-testid="kb-add-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1B1B1A] text-white text-[13px] font-medium px-4 py-2 hover:bg-black/85 transition"
          >
            <Plus className="w-4 h-4" /> {isAr ? 'إضافة' : 'Add'}
          </button>
        </header>

        {/* Tabs */}
        <div
          role="tablist"
          aria-label="Knowledge Base sections"
          className="flex items-center gap-1 border-b border-[#E5E1D8] mb-6"
          data-testid="kb-tabstrip"
        >
          {[
            { id: 'documents', label: isAr ? 'المستندات' : 'Documents' },
            { id: 'templates', label: isAr ? 'النماذج'    : 'Templates' },
            { id: 'sentinel',  label: 'Sentinel' },
          ].map((t) => (
            <button
              type="button"
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              aria-controls={`kb-panel-${t.id}`}
              data-testid={`kb-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={
                'px-4 py-2.5 text-[13.5px] font-medium border-b-2 -mb-[1px] transition ' +
                (tab === t.id
                  ? 'border-[#1B1B1A] text-[#1B1B1A]'
                  : 'border-transparent text-[#777] hover:text-[#1B1B1A]')
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div id={`kb-panel-${tab}`} role="tabpanel">
          {tab === 'documents' && (
            <DocumentsTab
              documents={documents}
              isAr={isAr}
              token={token}
              onMutated={() => { loadDocuments(); loadTemplates(); loadHealth(); }}
              onOpenUpload={() => setDrawerOpen(true)}
            />
          )}
          {tab === 'templates' && (
            <TemplatesTab
              templates={templates}
              isAr={isAr}
              token={token}
              onMutated={() => { loadDocuments(); loadTemplates(); loadHealth(); }}
              onOpenUpload={() => setDrawerOpen(true)}
            />
          )}
          {tab === 'sentinel' && (
            <SentinelTab
              health={health}
              user={user}
              token={token}
              isAr={isAr}
              onReload={loadHealth}
            />
          )}
        </div>
      </div>

      {drawerOpen && (
        <UploadDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onUploaded={onUploaded}
          token={token}
          isAr={isAr}
        />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Documents tab
// ────────────────────────────────────────────────────────────────────
function DocumentsTab({ documents, isAr, token, onMutated, onOpenUpload }) {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Build category list from sentinel_category. Unknown → "Uncategorized".
  const categories = useMemo(() => {
    const counts = new Map();
    for (const d of documents) {
      const name = (d.sentinel_category || d.category || 'Uncategorized').trim() || 'Uncategorized';
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [documents]);

  const filtered = useMemo(() => {
    return documents.filter((d) => {
      if (selectedCategory) {
        const cat = (d.sentinel_category || d.category || 'Uncategorized');
        if (cat !== selectedCategory) return false;
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!(d.filename || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [documents, selectedCategory, search]);

  return (
    <div data-testid="kb-documents-tab">
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="kb-documents-search"
          placeholder={isAr ? 'ابحث في المستندات...' : 'Search documents...'}
          className="w-full pl-9 pr-3 py-2.5 rounded-full border border-[#E5E1D8] bg-white text-[13.5px] placeholder:text-[#999] focus:outline-none focus:border-[#1B1B1A] transition"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
        <aside className="space-y-1" data-testid="kb-documents-categories">
          <CategoryItem
            label={isAr ? 'الكل' : 'All'}
            count={documents.length}
            active={!selectedCategory}
            onClick={() => setSelectedCategory(null)}
          />
          {categories.map((c) => (
            <CategoryItem
              key={c.name}
              label={c.name}
              count={c.count}
              active={selectedCategory === c.name}
              onClick={() => setSelectedCategory(c.name)}
            />
          ))}
        </aside>
        <div data-testid="kb-documents-grid">
          {filtered.length === 0 ? (
            <DocumentsEmptyState isAr={isAr} onUpload={onOpenUpload} />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {filtered.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  doc={doc}
                  token={token}
                  isAr={isAr}
                  onMutated={onMutated}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryItem({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`kb-category-${label.replace(/\s+/g, '-').toLowerCase()}`}
      className={
        'w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-md text-[13px] transition ' +
        (active ? 'bg-[#1B1B1A] text-white' : 'text-[#444] hover:bg-[#F3EFE6]')
      }
    >
      <span className="truncate">{label}</span>
      <span className={active ? 'text-white/70 text-[11.5px]' : 'text-[#999] text-[11.5px]'}>{count}</span>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────
// Status badge (Documents only — Templates always use ★ Active)
// ────────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  if (status === 'processing') {
    return (
      <span
        data-testid="status-badge-processing"
        className="inline-flex items-center gap-1 rounded-full bg-[#F3F0EA] text-[#555] text-[11px] px-2 py-0.5"
      >
        <Loader2 className="w-3 h-3 animate-spin" /> Processing
      </span>
    );
  }
  if (status === 'conflicted') {
    return (
      <span
        data-testid="status-badge-conflicted"
        className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 text-[11px] px-2 py-0.5 border border-amber-200"
      >
        <AlertTriangle className="w-3 h-3" /> Conflict
      </span>
    );
  }
  if (status === 'under_review') {
    return (
      <span
        data-testid="status-badge-under-review"
        className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 text-[11px] px-2 py-0.5 border border-blue-200"
      >
        Under Review
      </span>
    );
  }
  // default: active
  return (
    <span
      data-testid="status-badge-active"
      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] px-2 py-0.5 border border-emerald-200"
    >
      <CheckCircle className="w-3 h-3" /> Active
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────
// Document card
// ────────────────────────────────────────────────────────────────────
function DocumentCard({ doc, token, isAr, onMutated }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const moveToTemplates = async () => {
    setMenuOpen(false);
    try {
      await _json(`${API}/api/vault/documents/${doc.id}/move-to-templates`, { method: 'POST' }, token);
      onMutated();
    } catch { /* no-op */ }
  };
  const onDelete = async () => {
    setMenuOpen(false);
    try {
      await _json(`${API}/api/vault/documents/${doc.id}`, { method: 'DELETE' }, token);
      onMutated();
    } catch { /* no-op */ }
  };

  return (
    <article
      data-testid={`doc-card-${doc.id}`}
      data-status={doc.sentinel_status}
      className="rounded-lg border border-[#E5E1D8] bg-white p-4 hover:border-[#C8BFA8] transition"
    >
      <header className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-[14px] font-medium text-[#1B1B1A] truncate" data-testid="doc-card-name">
          <FileText className="inline w-4 h-4 mr-1 text-[#888]" />
          {doc.filename}
        </h3>
        <StatusBadge status={doc.sentinel_status} />
      </header>
      <ul className="text-[12px] text-[#666] space-y-0.5 mb-3">
        <li>{doc.sentinel_category || doc.category || 'Uncategorized'}</li>
        {doc.uploader_name && (
          <li data-testid="doc-card-uploader">
            {isAr ? `رفعها ${doc.uploader_name}` : `Uploaded by ${doc.uploader_name}`}
          </li>
        )}
        <li>{isAr ? 'تم التحديث' : 'Updated'} {formatDate(doc.updated_at)}</li>
        <li data-testid="doc-card-usage">{isAr ? `استُخدمت ${doc.usage_count || 0} مرات` : `Used ${doc.usage_count || 0} times`}</li>
      </ul>
      <footer className="flex items-center justify-between gap-2">
        <button
          type="button"
          data-testid="doc-card-preview"
          className="text-[12.5px] text-[#1B1B1A] hover:underline"
        >
          {isAr ? 'معاينة' : 'Preview'}
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="doc-card-more"
            aria-label="More actions"
            className="p-1 rounded hover:bg-[#F3EFE6]"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 z-20 mt-1 w-52 rounded-md border border-[#E5E1D8] bg-white shadow-md py-1 text-[13px]"
              data-testid="doc-card-menu"
            >
              <MenuRow onClick={moveToTemplates}>Move to Templates</MenuRow>
              <MenuRow disabled>Replace document</MenuRow>
              <MenuRow disabled>Download original</MenuRow>
              {doc.sentinel_status === 'conflicted' && (
                <MenuRow disabled>View conflict report</MenuRow>
              )}
              <div className="border-t border-[#E5E1D8] my-1" />
              <MenuRow danger onClick={onDelete}>Delete</MenuRow>
            </div>
          )}
        </div>
      </footer>
    </article>
  );
}

function MenuRow({ children, onClick, danger, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        'w-full text-left px-3 py-1.5 transition ' +
        (disabled ? 'text-[#bbb] cursor-not-allowed' :
          danger ? 'text-red-700 hover:bg-red-50' :
            'text-[#1B1B1A] hover:bg-[#F3EFE6]')
      }
    >
      {children}
    </button>
  );
}

function DocumentsEmptyState({ isAr, onUpload }) {
  return (
    <div
      data-testid="kb-documents-empty"
      className="rounded-lg border border-dashed border-[#D5CCB8] bg-white p-10 text-center"
    >
      <Folder className="w-9 h-9 text-[#B8AC8E] mx-auto mb-4" />
      <h3 className="text-[16px] font-medium text-[#1B1B1A] mb-2">
        {isAr ? 'لا توجد مستندات بعد' : 'No documents yet'}
      </h3>
      <p className="text-[13px] text-[#666] mb-1 max-w-md mx-auto">
        {isAr
          ? 'حمّل سياسات شركتك وإجراءاتها ومصفوفات الصلاحيات وأدلة الامتثال.'
          : 'Upload your company policies, procedures, authority matrices, and compliance manuals.'}
      </p>
      <p className="text-[13px] text-[#666] mb-5 max-w-md mx-auto">
        {isAr
          ? 'سيقرأها Sentinel ويصنّفها ويراقبها تلقائياً.'
          : 'Sentinel will read, categorize, and monitor them automatically.'}
      </p>
      <button
        type="button"
        onClick={onUpload}
        data-testid="kb-documents-empty-upload"
        className="inline-flex items-center gap-1.5 rounded-full bg-[#1B1B1A] text-white text-[13px] font-medium px-4 py-2 hover:bg-black/85"
      >
        <UploadCloud className="w-4 h-4" /> {isAr ? 'حمّل أول مستند' : 'Upload your first document'}
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Templates tab
// ────────────────────────────────────────────────────────────────────
function TemplatesTab({ templates, isAr, token, onMutated, onOpenUpload }) {
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState(null);

  const types = useMemo(() => {
    const counts = new Map();
    for (const t of templates) {
      const name = (t.sentinel_category || t.document_type || 'Other').trim() || 'Other';
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [templates]);

  const filtered = useMemo(() => templates.filter((t) => {
    if (selectedType) {
      const tt = t.sentinel_category || t.document_type || 'Other';
      if (tt !== selectedType) return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!(t.filename || '').toLowerCase().includes(q)) return false;
    }
    return true;
  }), [templates, selectedType, search]);

  return (
    <div data-testid="kb-templates-tab">
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="kb-templates-search"
          placeholder={isAr ? 'ابحث في النماذج...' : 'Search templates...'}
          className="w-full pl-9 pr-3 py-2.5 rounded-full border border-[#E5E1D8] bg-white text-[13.5px] placeholder:text-[#999] focus:outline-none focus:border-[#1B1B1A] transition"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
        <aside className="space-y-1" data-testid="kb-templates-types">
          <CategoryItem
            label={isAr ? 'الكل' : 'All'}
            count={templates.length}
            active={!selectedType}
            onClick={() => setSelectedType(null)}
          />
          {types.map((c) => (
            <CategoryItem
              key={c.name}
              label={c.name}
              count={c.count}
              active={selectedType === c.name}
              onClick={() => setSelectedType(c.name)}
            />
          ))}
        </aside>
        <div data-testid="kb-templates-grid">
          {filtered.length === 0 ? (
            <TemplatesEmptyState isAr={isAr} onUpload={onOpenUpload} />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {filtered.map((t) => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  token={token}
                  isAr={isAr}
                  onMutated={onMutated}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplateCard({ template, token, isAr, onMutated }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const moveToDocs = async () => {
    setMenuOpen(false);
    try {
      await _json(`${API}/api/vault/templates/${template.id}/move-to-documents`, { method: 'POST' }, token);
      onMutated();
    } catch { /* no-op */ }
  };
  const onDelete = async () => {
    setMenuOpen(false);
    try {
      await _json(`${API}/api/vault/documents/${template.id}`, { method: 'DELETE' }, token);
      onMutated();
    } catch { /* no-op */ }
  };
  return (
    <article
      data-testid={`tpl-card-${template.id}`}
      className="rounded-lg border border-[#E5E1D8] bg-white p-4 hover:border-[#C8BFA8] transition"
    >
      <header className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-[14px] font-medium text-[#1B1B1A] truncate" data-testid="tpl-card-name">
          <Layers className="inline w-4 h-4 mr-1 text-[#888]" />
          {template.filename}
        </h3>
        <span
          data-testid="tpl-card-active"
          className="inline-flex items-center gap-1 rounded-full bg-[#FFF7E1] text-[#7A5A00] text-[11px] px-2 py-0.5 border border-[#F0D88A]"
        >
          <Star className="w-3 h-3 fill-current" /> Active
        </span>
      </header>
      <ul className="text-[12px] text-[#666] space-y-0.5 mb-3">
        <li>{template.sentinel_category || template.document_type || 'Other'}</li>
        <li data-testid="tpl-card-usage">{isAr ? `استُخدم في ${template.usage_count || 0} مسودة` : `Used in ${template.usage_count || 0} drafts`}</li>
        <li>{isAr ? 'آخر استخدام' : 'Last used'} {template.last_used_at ? formatRelative(template.last_used_at) : (isAr ? 'أبداً' : 'Never')}</li>
      </ul>
      <footer className="flex items-center justify-between gap-2">
        <button
          type="button"
          data-testid="tpl-card-preview"
          className="text-[12.5px] text-[#1B1B1A] hover:underline"
        >
          {isAr ? 'معاينة' : 'Preview'}
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="tpl-card-more"
            aria-label="More actions"
            className="p-1 rounded hover:bg-[#F3EFE6]"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 z-20 mt-1 w-52 rounded-md border border-[#E5E1D8] bg-white shadow-md py-1 text-[13px]" data-testid="tpl-card-menu">
              <MenuRow onClick={moveToDocs}>Move to Documents</MenuRow>
              <MenuRow disabled>Replace template</MenuRow>
              <MenuRow disabled>Download original</MenuRow>
              <MenuRow disabled>View usage history</MenuRow>
              <div className="border-t border-[#E5E1D8] my-1" />
              <MenuRow danger onClick={onDelete}>Delete</MenuRow>
            </div>
          )}
        </div>
      </footer>
    </article>
  );
}

function TemplatesEmptyState({ isAr, onUpload }) {
  return (
    <div
      data-testid="kb-templates-empty"
      className="rounded-lg border border-dashed border-[#D5CCB8] bg-white p-10 text-center"
    >
      <Layers className="w-9 h-9 text-[#B8AC8E] mx-auto mb-4" />
      <h3 className="text-[16px] font-medium text-[#1B1B1A] mb-2">
        {isAr ? 'لا توجد نماذج بعد' : 'No templates yet'}
      </h3>
      <p className="text-[13px] text-[#666] mb-1 max-w-md mx-auto">
        {isAr
          ? 'حمّل نماذج عقود شركتك المعتمدة وسيستخدمها Scribe كمرجع لكل مسودة.'
          : "Upload your firm's approved contract templates and Scribe will use them as the structural reference for every draft."}
      </p>
      <p className="text-[13px] text-[#666] mb-5 max-w-md mx-auto">
        {isAr
          ? 'كلما أضفت أكثر، اقتربت بينة من أسلوب شركتك.'
          : 'The more you add, the more Bayenah sounds like your firm.'}
      </p>
      <button
        type="button"
        onClick={onUpload}
        data-testid="kb-templates-empty-upload"
        className="inline-flex items-center gap-1.5 rounded-full bg-[#1B1B1A] text-white text-[13px] font-medium px-4 py-2 hover:bg-black/85"
      >
        <UploadCloud className="w-4 h-4" /> {isAr ? 'حمّل أول نموذج' : 'Upload your first template'}
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Sentinel tab — health dashboard + Guardian section + chat
// ────────────────────────────────────────────────────────────────────
function SentinelTab({ health, user, token, isAr }) {
  const hasGuardian = Boolean(health?.guardian_enabled);
  return (
    <div data-testid="kb-sentinel-tab" className="space-y-8">
      <SentinelDashboard health={health} isAr={isAr} />
      {hasGuardian
        ? (health.conflicted_count > 0
            ? <GuardianPanel health={health} token={token} isAr={isAr} />
            : <GuardianEmptyClear isAr={isAr} />)
        : <GuardianUpsell isAr={isAr} />}
      <SentinelChat token={token} isAr={isAr} />
    </div>
  );
}

function SentinelDashboard({ health, isAr }) {
  return (
    <section data-testid="kb-sentinel-dashboard" className="rounded-lg border border-[#E5E1D8] bg-white p-6">
      <header className="mb-4">
        <h2 className="text-[18px] font-semibold text-[#1B1B1A] flex items-center gap-2">
          <Shield className="w-4 h-4" /> Sentinel
        </h2>
        <p className="text-[12.5px] text-[#666] mt-0.5">
          {isAr ? 'حارس قاعدة معرفتك' : 'Your Knowledge Base guardian'}
        </p>
      </header>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="sentinel-health-grid">
        <HealthCard num={health.total_documents || 0} label={isAr ? 'مستندات' : 'Documents'} />
        <HealthCard num={health.total_templates || 0} label={isAr ? 'نماذج' : 'Templates'} />
        <HealthCard
          num={health.conflicted_count || 0}
          label={isAr ? 'تعارضات' : 'Conflicts'}
          highlight={(health.conflicted_count || 0) > 0}
        />
        <HealthCard
          num={health.processing_count || 0}
          label={isAr ? 'قيد المعالجة' : 'Processing'}
          spinner={(health.processing_count || 0) > 0}
        />
      </div>
      <div data-testid="sentinel-recent-activity">
        <h3 className="text-[13px] font-semibold text-[#1B1B1A] mb-2">
          {isAr ? 'النشاط الأخير' : 'Recent activity'}
        </h3>
        {(health.recent_activity || []).length === 0 ? (
          <p className="text-[12px] text-[#888]">{isAr ? 'لا يوجد نشاط بعد.' : 'No activity yet.'}</p>
        ) : (
          <ul className="space-y-1">
            {health.recent_activity.slice(0, 6).map((a, i) => (
              <li key={i} className="text-[12.5px] text-[#444] flex items-center gap-2">
                <span className="text-[#999]">·</span>
                <span className="capitalize">{a.action}</span>
                <span className="text-[#999]">—</span>
                <span className="truncate">{a.doc_name}</span>
                <span className="ml-auto text-[11px] text-[#999]">{formatRelative(a.timestamp)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function HealthCard({ num, label, highlight, spinner }) {
  return (
    <div
      data-testid={`sentinel-health-${label.toLowerCase()}`}
      className={
        'rounded-md border p-3 text-center transition ' +
        (highlight ? 'border-amber-300 bg-amber-50' : 'border-[#E5E1D8] bg-[#FAFAF8]')
      }
    >
      <div className={'text-[24px] font-semibold flex items-center justify-center gap-1.5 ' + (highlight ? 'text-amber-700' : 'text-[#1B1B1A]')}>
        {spinner && <Loader2 className="w-4 h-4 animate-spin text-[#666]" />}
        {num}
      </div>
      <div className="text-[11.5px] text-[#666] mt-0.5 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function GuardianEmptyClear({ isAr }) {
  return (
    <section
      data-testid="kb-guardian-clear"
      className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 flex items-center gap-3"
    >
      <CheckCircle className="w-5 h-5 text-emerald-700" />
      <p className="text-[13.5px] text-emerald-800">
        {isAr ? 'لا توجد تعارضات. Sentinel Guardian فحص قاعدة معرفتك بالكامل.' : 'No conflicts detected. Sentinel Guardian has scanned your full Knowledge Base.'}
      </p>
    </section>
  );
}

function GuardianPanel({ health, token, isAr }) {
  // Guardian panel surfaces conflicts. The agent stores per-doc
  // sentinel_conflicts; without an aggregated list endpoint we surface
  // the recent_activity drill-down hint here and link to the Documents
  // tab where status badges expose conflict_count.
  return (
    <section
      data-testid="kb-guardian-panel"
      className="rounded-lg border border-amber-300 bg-amber-50 p-5"
    >
      <h3 className="text-[15px] font-semibold text-amber-800 flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4" /> {health.conflicted_count} {isAr ? 'تعارض' : 'conflict'}{health.conflicted_count > 1 ? 's' : ''} {isAr ? 'مكتشف' : 'detected'}
      </h3>
      <p className="text-[13px] text-amber-900">
        {isAr
          ? 'افتح علامة تبويب المستندات لرؤية التقارير لكل مستند، وقم بحلها هناك. تنطبق التغييرات تلقائياً على كلا المستندين.'
          : 'Open the Documents tab to see the report on each affected document and resolve from either side — Sentinel mirrors the resolution onto the conflicting document automatically.'}
      </p>
    </section>
  );
}

function GuardianUpsell({ isAr }) {
  return (
    <section
      data-testid="kb-guardian-upsell"
      className="rounded-lg border border-[#E5E1D8] bg-white p-6"
    >
      <header className="flex items-center gap-2 mb-2">
        <Lock className="w-4 h-4 text-[#1B1B1A]" />
        <h3 className="text-[15px] font-semibold text-[#1B1B1A]">Sentinel Guardian</h3>
      </header>
      <p className="text-[13px] text-[#555] mb-4 max-w-xl">
        {isAr
          ? 'يكتشف Guardian التناقضات بين مستندات شركتك قبل أن تسبب مشاكل حوكمة.'
          : 'Sentinel Guardian automatically detects contradictions between your company documents before they cause governance issues.'}
      </p>
      <div className="rounded-md border border-[#E5E1D8] bg-[#FAFAF8] p-4 mb-4" data-testid="kb-guardian-sample" style={{ filter: 'blur(2px)' }} aria-hidden="true">
        <div className="text-[12.5px] text-[#1B1B1A] mb-1">HR Policy 2025 · Article 7</div>
        <div className="text-[11px] text-[#999] mb-1">conflicts with</div>
        <div className="text-[12.5px] text-[#1B1B1A] mb-1">Authority Matrix · Article 3</div>
        <div className="text-[12px] text-[#666]">████████████████████████████████</div>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-[12px] text-[#666]">{isAr ? 'متاح في خطة Enterprise+' : 'Available on Enterprise+'}</span>
        <a
          href="mailto:sales@bayenah.com"
          data-testid="kb-guardian-contact"
          className="inline-flex items-center gap-1 rounded-full bg-[#1B1B1A] text-white text-[12.5px] px-3.5 py-1.5 hover:bg-black/85"
        >
          {isAr ? 'تواصل معنا' : 'Talk to us'} →
        </a>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────
// Sentinel chat
// ────────────────────────────────────────────────────────────────────
function SentinelChat({ token, isAr }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    const q = input.trim();
    if (!q || loading) return;
    setInput('');
    setLoading(true);
    const userMsg = { id: `u-${Date.now()}`, role: 'user', content: q };
    setMessages((m) => [...m, userMsg]);
    try {
      const r = await _json(
        `${API}/api/sentinel/query`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: q, language: isAr ? 'ar' : 'en' }),
        },
        token,
      );
      setMessages((m) => [...m, {
        id: `s-${Date.now()}`, role: 'sentinel',
        content: r.answer || '', sources: r.sources || [],
      }]);
    } catch {
      setMessages((m) => [...m, {
        id: `s-${Date.now()}`, role: 'sentinel',
        content: isAr ? 'حدث خطأ. حاول مرة أخرى.' : 'Something went wrong. Please try again.',
        sources: [],
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="kb-sentinel-chat" className="rounded-lg border border-[#E5E1D8] bg-white p-5">
      <div className="text-center text-[12px] tracking-wide uppercase text-[#888] mb-3" data-testid="kb-sentinel-chat-divider">
        {isAr ? 'اسأل Sentinel عن قاعدة معرفتك' : 'Ask Sentinel anything about your Knowledge Base'}
      </div>
      <div className="space-y-3 mb-3 max-h-[320px] overflow-y-auto" data-testid="kb-sentinel-chat-messages">
        {messages.length === 0 ? (
          <p className="text-[12.5px] text-[#999] text-center py-4">
            {isAr ? 'لم تبدأ محادثة بعد.' : 'No messages yet.'}
          </p>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              data-testid={`sentinel-msg-${m.role}`}
              className={m.role === 'user' ? 'text-right' : 'text-left'}
            >
              <div className={
                'inline-block max-w-[85%] rounded-md px-3 py-2 text-[13px] ' +
                (m.role === 'user' ? 'bg-[#1B1B1A] text-white' : 'bg-[#F3EFE6] text-[#1B1B1A]')
              }>
                {m.content}
                {m.sources && m.sources.length > 0 && (
                  <ul className="mt-2 space-y-0.5 text-[11px] opacity-75">
                    {m.sources.slice(0, 3).map((s) => (
                      <li key={s.doc_id}>· {s.doc_name}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      <form onSubmit={onSubmit} className="flex items-center gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={isAr ? 'اسأل Sentinel عن قاعدة المعرفة...' : 'Ask Sentinel about your Knowledge Base...'}
          data-testid="kb-sentinel-chat-input"
          className="flex-1 px-3 py-2 rounded-full border border-[#E5E1D8] text-[13px] placeholder:text-[#999] focus:outline-none focus:border-[#1B1B1A]"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          data-testid="kb-sentinel-chat-send"
          className="inline-flex items-center justify-center rounded-full bg-[#1B1B1A] text-white p-2 disabled:opacity-40"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────
// Upload drawer
// ────────────────────────────────────────────────────────────────────
function UploadDrawer({ open, onClose, onUploaded, token, isAr }) {
  const [files, setFiles] = useState([]);
  const [contentType, setContentType] = useState('document');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');     // e.g. "Uploading 2 of 3..."
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  const MAX = 10 * 1024 * 1024;

  // Merge newly picked/dropped files into the current list, dedupe by
  // name+size, and surface a single line of validation feedback for any
  // rejected oversize files.
  const addFiles = (incoming) => {
    setErr('');
    const list = Array.from(incoming || []);
    if (list.length === 0) return;
    const tooBig = list.filter((f) => f.size > MAX).map((f) => f.name);
    const ok = list.filter((f) => f.size <= MAX);
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}::${f.size}`));
      const merged = [...prev];
      for (const f of ok) {
        const key = `${f.name}::${f.size}`;
        if (!seen.has(key)) { merged.push(f); seen.add(key); }
      }
      return merged;
    });
    if (tooBig.length > 0) {
      setErr(
        (isAr
          ? `تم تجاوز الحجم الأقصى (10 ميغابايت): ${tooBig.join(', ')}`
          : `Skipped (over 10MB): ${tooBig.join(', ')}`).slice(0, 240),
      );
    }
  };

  const onPick = (e) => {
    addFiles(e.target.files);
    // Reset so picking the same file twice still fires onChange.
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  const removeAt = (i) => {
    if (uploading) return;
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
  };

  const submit = async () => {
    if (files.length === 0 || !contentType || uploading) return;
    setUploading(true);
    setErr('');
    const failed = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress(
        isAr
          ? `جارٍ رفع ${i + 1} من ${files.length}...`
          : `Uploading ${i + 1} of ${files.length}...`,
      );
      try {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('content_type', contentType);
        const r = await fetch(`${API}/api/vault/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!r.ok) {
          // Defensive: some monitoring scripts (Emergent RUM, devtools,
          // extensions) read the response body before we get a chance.
          // Clone first so a drained stream doesn't crash error reporting.
          let detail = '';
          try { detail = await r.clone().text(); }
          catch { detail = r.statusText || `HTTP ${r.status}`; }
          throw new Error(detail || `HTTP ${r.status}`);
        }
      } catch (e) {
        failed.push({ name: f.name, msg: String(e?.message || e || 'failed').slice(0, 120) });
      }
    }
    setUploading(false);
    setProgress('');
    if (failed.length === 0) {
      setFiles([]);
      onUploaded?.();
    } else {
      // Keep the drawer open so the user sees what failed. Refresh the
      // KB so successful uploads still appear behind the drawer.
      onUploaded?.();
      setErr(
        (isAr
          ? `فشل رفع ${failed.length} ملف: `
          : `${failed.length} file${failed.length > 1 ? 's' : ''} failed: `)
        + failed.map((x) => x.name).join(', '),
      );
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40" data-testid="upload-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside
        dir={isAr ? 'rtl' : 'ltr'}
        className="absolute top-0 right-0 h-full w-full sm:w-[440px] bg-white border-l border-[#E5E1D8] p-6 overflow-y-auto"
      >
        <header className="flex items-center justify-between mb-5">
          <h2 className="text-[16px] font-semibold text-[#1B1B1A]" data-testid="upload-drawer-title">
            {isAr ? 'إضافة إلى قاعدة المعرفة' : 'Add to Knowledge Base'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="upload-drawer-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <label
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          htmlFor="upload-input"
          data-testid="upload-dropzone"
          className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-[#D5CCB8] bg-[#FAFAF8] p-8 cursor-pointer hover:border-[#B8AC8E] transition"
        >
          <UploadCloud className="w-8 h-8 text-[#888]" />
          <span className="text-[13px] text-[#1B1B1A] font-medium">
            {isAr ? 'اسحب الملفات هنا أو انقر للتصفح' : 'Drag files here or click to browse'}
          </span>
          <span className="text-[11.5px] text-[#888]">
            {isAr ? 'PDF · DOCX · TXT · ≤ 10 ميغابايت · ملفات متعددة مدعومة' : 'PDF · DOCX · TXT · ≤ 10MB · multiple files supported'}
          </span>
        </label>
        <input
          ref={inputRef}
          id="upload-input"
          type="file"
          multiple
          accept=".pdf,.docx,.txt"
          onChange={onPick}
          className="hidden"
          data-testid="upload-file-input"
        />

        {files.length > 0 && (
          <ul className="mt-3 space-y-1.5" data-testid="upload-file-list">
            {files.map((f, i) => (
              <li
                key={`${f.name}-${f.size}-${i}`}
                data-testid="upload-file-preview"
                className="flex items-center justify-between gap-2 rounded-md border border-[#E5E1D8] bg-white px-3 py-2 text-[12.5px]"
              >
                <span className="truncate flex-1 min-w-0">
                  <FileText className="inline w-3.5 h-3.5 mr-1 text-[#888]" />{f.name}
                </span>
                <span className="text-[#888] flex-shrink-0">{formatBytes(f.size)}</span>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  disabled={uploading}
                  aria-label={isAr ? 'إزالة' : 'Remove'}
                  data-testid={`upload-file-remove-${i}`}
                  className="text-[#999] hover:text-red-700 disabled:opacity-40 flex-shrink-0 p-0.5"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6" data-testid="upload-content-type">
          <h3 className="text-[13px] font-medium text-[#1B1B1A] mb-3">
            {isAr ? 'ما نوع هذه الملفات؟' : 'What are these?'}
          </h3>
          <div role="radiogroup" aria-label="Content type" className="space-y-2">
            {[
              { v: 'document', t: isAr ? 'مستند' : 'Document',
                desc: isAr ? 'سياسات، إجراءات، مصفوفات صلاحيات، أدلة امتثال'
                           : 'Policies, procedures, authority matrices, compliance manuals' },
              { v: 'template', t: isAr ? 'نموذج' : 'Template',
                desc: isAr ? 'هياكل عقود معتمدة يستخدمها Scribe في كل مسودة'
                           : 'Approved contract structures Scribe uses for every draft' },
            ].map((opt) => (
              <label
                key={opt.v}
                data-testid={`upload-radio-${opt.v}`}
                aria-label={opt.t}
                className={
                  'flex items-start gap-3 rounded-md border p-3 cursor-pointer transition ' +
                  (contentType === opt.v
                    ? 'border-[#1B1B1A] bg-[#FAFAF8]'
                    : 'border-[#E5E1D8] hover:border-[#C8BFA8]')
                }
              >
                <input
                  type="radio"
                  name="content_type"
                  value={opt.v}
                  checked={contentType === opt.v}
                  onChange={() => setContentType(opt.v)}
                  className="mt-1"
                />
                <div>
                  <div className="text-[13.5px] font-medium text-[#1B1B1A]">{opt.t}</div>
                  <div className="text-[12px] text-[#666]">{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <p className="mt-5 text-[12px] text-[#666] flex items-start gap-2" data-testid="upload-sentinel-notice">
          <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          {isAr
            ? 'سيقرأها Sentinel ويصنّفها ويفحص التعارضات تلقائياً.'
            : 'Sentinel will read them, categorize them, and check for conflicts automatically.'}
        </p>

        {err && <p className="mt-3 text-[12px] text-red-700" data-testid="upload-error">{err}</p>}

        <button
          type="button"
          onClick={submit}
          disabled={files.length === 0 || !contentType || uploading}
          data-testid="upload-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1B1B1A] text-white text-[13.5px] font-medium px-4 py-2.5 hover:bg-black/85 disabled:opacity-40"
        >
          {uploading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> {progress || (isAr ? 'جاري الرفع...' : 'Uploading...')}</>
          ) : (
            isAr
              ? (files.length > 1 ? `رفع ${files.length} ملفات` : 'رفع')
              : (files.length > 1 ? `Upload ${files.length} files` : 'Upload file')
          )}
        </button>
      </aside>
    </div>
  );
}

export default VaultPage;
